import { Player } from '@/types/tournament';
import { assignPlayersToCourts } from '@/utils/tournamentLogic';
import { toast } from 'sonner';
import { ExtendedTournamentState } from './types';
import { generateAmericanoPartnerships, assignAmericanoCourts } from './americanoLogic';

export const createGoToPreviousRound = (
  setTournamentState: React.Dispatch<React.SetStateAction<ExtendedTournamentState>>,
  language: string
) => {
  return () => {
    let restoredInfo = '';
    
    setTournamentState(prevState => {
      if (prevState.currentRound <= 1) {
        // Can't go back from round 1
        return prevState;
      }

      const previousRound = prevState.currentRound - 1;
      
      if (prevState.tournamentType === 'americano') {
        // For Americano, restore previous round scores properly
        const updatedPlayers = prevState.americanoPlayers.map(player => {
          // Only keep scores up to the previous round
          const restoredRoundScores = player.roundScores ? player.roundScores.slice(0, previousRound) : [];
          const totalPoints = restoredRoundScores.reduce((sum, score) => sum + score, 0);
          
          return {
            ...player,
            roundScores: restoredRoundScores,
            points: totalPoints,
            gamesPlayed: restoredRoundScores.length
          };
        });
        
        // Restore original match assignments for the previous round
        let matches: any[] = [];
        let restoredMatches = 0;
        
        // Use stored match assignments if available
        if (prevState.roundAmericanoMatches && prevState.roundAmericanoMatches[previousRound - 1]) {
          const roundData = prevState.roundAmericanoMatches[previousRound - 1];
          matches = [...(roundData.matches || [])];
          
          // If we have stored results for this round, apply them to the matches
          if (roundData.results && roundData.results.length > 0) {
            const storedResults = roundData.results;
            matches = matches.map((match, index) => {
              const storedResult = storedResults[index];
              if (storedResult && storedResult.score) {
                restoredMatches++;
                return {
                  ...match,
                  score: storedResult.score
                };
              }
              return match;
            });
          }
          
          console.log(`Restoring ${matches.length} original matches for round ${previousRound}, ${restoredMatches} with scores`);
        } else {
          // If no stored assignments, generate fresh matches
          const partnerships = generateAmericanoPartnerships(updatedPlayers, previousRound);
          matches = assignAmericanoCourts(partnerships);
          console.log(`Generated fresh matches for round ${previousRound}:`, matches);
        }
        
        // Set restored info message for Americano
        if (restoredMatches > 0) {
          restoredInfo = language === 'no' 
            ? ` (${restoredMatches} kamper med resultater gjenopprettet)`
            : ` (${restoredMatches} matches with results restored)`;
        }
        
        return {
          ...prevState,
          currentRound: previousRound,
          americanoMatches: matches,
          americanoPlayers: updatedPlayers,
          isRoundActive: true,
          isFinished: false
        };
      } else {
        // For Mexicano, restore previous round scores and court assignments
        const updatedPlayers = prevState.players.map(player => {
          // Only keep scores up to the previous round
          const restoredRoundScores = player.roundScores ? player.roundScores.slice(0, previousRound) : [];
          const totalPoints = restoredRoundScores.reduce((sum, score) => sum + score, 0);
          
          return {
            ...player,
            roundScores: restoredRoundScores,
            points: totalPoints,
            matchesPlayed: restoredRoundScores.length
          };
        });
        
        // Use stored court assignments if available, otherwise generate fresh ones
        let courts: Player[][];
        if (prevState.roundCourts && prevState.roundCourts[previousRound - 1]) {
          const roundData = prevState.roundCourts[previousRound - 1];
          courts = roundData.courts || [];
          console.log(`Restoring original court assignments for round ${previousRound}:`, courts);
        } else {
          const { courts } = assignPlayersToCourts(updatedPlayers);
          console.log(`Generated fresh court assignments for round ${previousRound}:`, courts);
        }
        
        // Restore results array to only include completed rounds
        const restoredResults = prevState.results.slice(0, previousRound);
        
        // Set restored info message for Mexicano
        if (restoredResults.length > 0 && restoredResults[previousRound - 1]) {
          const restoredMatches = restoredResults[previousRound - 1].length;
          restoredInfo = language === 'no' 
            ? ` (${restoredMatches} kamper med resultater gjenopprettet)`
            : ` (${restoredMatches} matches with results restored)`;
          console.log(`Restoring ${restoredMatches} matches with scores for Mexicano round ${previousRound}:`, restoredResults[previousRound - 1]);
        } else {
          console.log(`No stored results found for Mexicano round ${previousRound}`);
        }
        
        return {
          ...prevState,
          currentRound: previousRound,
          courts,
          players: updatedPlayers,
          results: restoredResults,
          isActive: true
        };
      }
    });
    
    const baseMessage = language === 'no' ? 'Gikk tilbake til forrige runde' : 'Went back to previous round';
    toast.info(baseMessage + restoredInfo);
  };
};
